import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import axios from "axios";
import { apiUrlShop, DEFAULT_STALE_TIME, DEFAULT_CACHE_TIME } from "../../shared/constants";
import { fetchData as fetchDataNode } from "../nodeApi/fetchData";
import useAuthStore from "../../shared/store/useAuthStore";
import { useMemo } from "react";

const apiUrl = window._env_?.REACT_APP_BACKEND_URL;

const fetchData = async (endpoint, params, baseUrl) => {

  const token = useAuthStore.getState().accessToken;
  const url = `${baseUrl || apiUrl}${endpoint}`;

  try {
    const { data } = await axios.get(url, {
      params,
      timeout: 30000,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return data;
  } catch (error) {
    if (error.code === "ECONNABORTED" || (error.message && error.message.includes("timeout"))) {
      console.error("API request timed out");
      throw new Error("Request timed out. Please try again.");
    }
    throw error;
  }
};

export const useEvents = () => {
  return useInfiniteQuery({
    queryKey: ["events"],
    queryFn: async ({ pageParam = 1 }) => {
      const data = await fetchData(`collab-events?page=${pageParam}`);
      data.data = data.data.filter(event => event.event_name !== null);
      return data;
    },
    getNextPageParam: (lastPage) =>
      lastPage.current_page < lastPage.last_page
        ? lastPage.current_page + 1
        : undefined,
    staleTime: DEFAULT_STALE_TIME,
    cacheTime: DEFAULT_CACHE_TIME,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });
};


// export const useCourses = (filters) => {
//   return useInfiniteQuery({
//     queryKey: ["courses", filters],
//     queryFn: async ({ pageParam = 1 }) => {
//       const res = await fetchDataNode(
//         `collab-courses?page=${pageParam}&limit=10`,
//         filters,
//       );

//       return {
//         data: res?.courses?.data || [],
//         currentPage: res?.courses?.pagination?.current_page,
//         totalPages: res?.courses?.pagination?.totalPages,
//       };
//     },
//     getNextPageParam: (lastPage) => {
//       if (lastPage.currentPage < lastPage.totalPages) {
//         return lastPage.currentPage + 1;
//       }
//       return undefined;
//     },
//     staleTime: DEFAULT_STALE_TIME,
//     cacheTime: DEFAULT_CACHE_TIME,
//     refetchOnWindowFocus: false,
//     refetchOnMount: true,
//   });
// };




export const useCourses = (filters, limit = 10) => {
  const query = useInfiniteQuery({
    queryKey: ["courses", filters, limit],
    queryFn: async ({ pageParam = 1 }) => {
      // ── Step 1: Fetch course list ──────────────────────────────────────────
      const res = await fetchDataNode(
        `collab-courses?page=${pageParam}&limit=${limit}`,
        filters
      );

      const courses = res?.courses?.data || [];

      // ── Step 2: Extract IDs using the correct 'id' field from API ─────────
      const ids = courses.map((course) => course?.id).filter(Boolean);

      // ── Step 3: Fetch ratings for all IDs in parallel ─────────────────────
      const ratingResults = await Promise.allSettled(
        ids.map((id) => fetchDataNode(`ratings/event/${id}`))
      );

      // ── Step 4: Build a ratingMap keyed by course id ──────────────────────
      const ratingMap = {};
      ids.forEach((id, index) => {
        const result = ratingResults[index];
        ratingMap[id] =
          result.status === "fulfilled" ? result.value ?? {} : {};
      });

      // ── Step 5: Merge ratingDetails into each course object ───────────────
      const enrichedCourses = courses.map((course) => ({
        ...course,
        ratingDetails: ratingMap[course?.id] ?? {},
      }));

      return {
        data: enrichedCourses,
        currentPage: res?.courses?.pagination?.current_page,
        totalPages: res?.courses?.pagination?.totalPages,
      };
    },
    getNextPageParam: (lastPage) => {
      if (lastPage.currentPage < lastPage.totalPages) {
        return lastPage.currentPage + 1;
      }
      return undefined;
    },
    staleTime: DEFAULT_STALE_TIME,
    cacheTime: DEFAULT_CACHE_TIME,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  const flatData = useMemo(() => {
    return query.data?.pages?.flatMap((page) => page?.data) || [];
  }, [query.data]);

  return {
    ...query,
    flatData,
  };
};
export const useCourseDetail = (id) => {
  return useQuery({
    queryKey: ["coursedetail", id],
    queryFn: async () => {
      const data = await fetchDataNode(`courses-by-slug/${id}`);
      data.related_courses = data.related_courses.sort((a, b) => b.is_featured - a.is_featured);
      return data;
    },
    enabled: !!id,
    staleTime: DEFAULT_STALE_TIME,
    cacheTime: DEFAULT_CACHE_TIME,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });
}

export const useCourseDetailPro = (filters) => {
  return useQuery({
    queryKey: ["new-course-pro-list", filters],
    queryFn: async () => {
      const data = await fetchDataNode(`new-course-pro-list`, filters);
      return data;
    },
    staleTime: DEFAULT_STALE_TIME,
    cacheTime: DEFAULT_CACHE_TIME,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });
}


export const useNewCourseDetail = (filter) => {
  return useQuery({
    queryKey: ["new-course-details", filter],
    queryFn: () => fetchDataNode("new-course-details", filter),
    staleTime: DEFAULT_STALE_TIME,
    cacheTime: DEFAULT_CACHE_TIME,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });
}

export const useNewCourseDetailPro = (filter) => {
  return useQuery({
    queryKey: ["new-course-pro-details", filter],
    queryFn: () => fetchDataNode("new-course-pro-details", filter),
    staleTime: DEFAULT_STALE_TIME,
    cacheTime: DEFAULT_CACHE_TIME,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });
}

export const useCourseCategory = () => {
  return useQuery({
    queryKey: ["category"],
    queryFn: () => fetchDataNode("collab-categories"),
    staleTime: DEFAULT_STALE_TIME,
    cacheTime: DEFAULT_CACHE_TIME,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });
};


export const useEventDetail = (id) => {
  return useQuery({
    queryKey: ["eventdetail", id],
    queryFn: async () => {
      const data = await fetchData(`collab-event-view/${id}`);
      return data;
    },
    enabled: !!id,
    staleTime: DEFAULT_STALE_TIME,
    cacheTime: DEFAULT_CACHE_TIME,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });
}

export const useEventCategory = () => {
  return useQuery({
    queryKey: ["eventcategory"],
    queryFn: () => fetchData("collab-events-categories"),
    staleTime: DEFAULT_STALE_TIME,
    cacheTime: DEFAULT_CACHE_TIME,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });
};

export const useApartmentList = () => {
  return useQuery({
    queryKey: ["apartment_list"],
    queryFn: () => fetchDataNode("student-course-apartment-list"),
    staleTime: DEFAULT_STALE_TIME,
    cacheTime: DEFAULT_CACHE_TIME,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });
};


export const useCourseApartmentList = (filters) => {
  return useQuery({
    queryKey: ["course_apartment_list", filters],
    queryFn: () => fetchDataNode("collab-apartment-by-course", filters),
    staleTime: DEFAULT_STALE_TIME,
    cacheTime: DEFAULT_CACHE_TIME,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });
};

export const useEventsApartmentList = (filters) => {
  return useQuery({
    queryKey: ["event_apartment_list", filters],
    queryFn: () => fetchData("collab-apartment-by-event", filters),
    staleTime: DEFAULT_STALE_TIME,
    cacheTime: DEFAULT_CACHE_TIME,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });
};


export const usePriceCourseDetail = (params) => {
  return useQuery({
    queryKey: ["collab-apartment-details-courses", params],
    queryFn: () => fetchDataNode(`collab-apartment-details/${params?.apartmentName}/${params?.title}`),
    staleTime: DEFAULT_STALE_TIME,
    cacheTime: DEFAULT_CACHE_TIME,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });
};

export const useNewCourseDetail1 = (filter) => {
  return useQuery({
    queryKey: ["new-course-assign-details", filter],
    queryFn: () => fetchDataNode(`new-course-assign-details`, filter),
    enabled: !!filter?.slug,
    staleTime: DEFAULT_STALE_TIME,
    cacheTime: DEFAULT_CACHE_TIME,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });
};

export const useNewCourseDetailPro1 = (filter) => {
  return useQuery({
    queryKey: ["new-course-pro-assign-details", filter],
    queryFn: () => fetchDataNode(`new-course-pro-assign-details`, filter),
    enabled: !!filter?.slug,
    staleTime: DEFAULT_STALE_TIME,
    cacheTime: DEFAULT_CACHE_TIME,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });
};

export const usePriceEventDetail = (params) => {
  return useQuery({
    queryKey: ["collab-apartment-details-events", params],
    queryFn: () => fetchData(`collab-apartment-details-event/${params?.apartmentName}/${params?.title}`),
    staleTime: DEFAULT_STALE_TIME,
    cacheTime: DEFAULT_CACHE_TIME,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });
};

export const useApartmentListForCourse = (filter) => {
  return useQuery({
    queryKey: ["collab-apartment-name-list", filter],
    queryFn: () => fetchDataNode(`collab-apartment-name-list`, filter),
    staleTime: DEFAULT_STALE_TIME,
    cacheTime: DEFAULT_CACHE_TIME,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });
};

export const useStudentCourseList = () => {
  return useQuery({
    queryKey: ["student_course_name_list"],
    queryFn: () => fetchDataNode(`student-course-name-list`),
    staleTime: DEFAULT_STALE_TIME,
    cacheTime: DEFAULT_CACHE_TIME,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });
};


export const useTrainerList = () => {
  return useQuery({
    queryKey: ["getUnassignedTrainers"],
    queryFn: () => fetchData(`get-unassigned-trainers`),
    staleTime: DEFAULT_STALE_TIME,
    cacheTime: DEFAULT_CACHE_TIME,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });
};


export const useFilterApartmentlist = (id) => {
  return useQuery({
    queryKey: ["unassigned-apartments", id],
    queryFn: () => fetchData(`get-unassigned-apartments/${id}`),
    staleTime: DEFAULT_STALE_TIME,
    cacheTime: DEFAULT_CACHE_TIME,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });
};

export const useCoordinatorList = () => {
  return useQuery({
    queryKey: ["coordinator-user"],
    queryFn: () => fetchData(`coordinator-user`),
    staleTime: DEFAULT_STALE_TIME,
    cacheTime: DEFAULT_CACHE_TIME,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });
};

export const useUnassignCoordinator = () => {
  return useQuery({
    queryKey: ["unassigned-coordinator"],
    queryFn: () => fetchData(`unassigned-coordinator`),
    staleTime: DEFAULT_STALE_TIME,
    cacheTime: DEFAULT_CACHE_TIME,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });
};

export const useUnassignApartCord = () => {
  return useQuery({
    queryKey: ["unassigned-coordinator-apartments"],
    queryFn: () => fetchDataNode(`unassigned-coordinator-apartments`),
    staleTime: DEFAULT_STALE_TIME,
    cacheTime: DEFAULT_CACHE_TIME,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });
};

export const useTranerUnderCoorList = (id) => {
  return useQuery({
    queryKey: ["coordinator-apartment-count", id],
    queryFn: () => fetchData(`coordinator-apartment-count/${id}`),
    staleTime: DEFAULT_STALE_TIME,
    cacheTime: DEFAULT_CACHE_TIME,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });
};

export const useAvailableApartmentsForPayout = (filters) => {
  return useQuery({
    queryKey: ["available-apartments-for-payout", filters],
    queryFn: () => fetchData("available-apartments", filters),
    staleTime: DEFAULT_STALE_TIME,
    cacheTime: DEFAULT_CACHE_TIME,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });
};


export const useCountryList = () => {
  return useQuery({
    queryKey: ["countries-list"],
    queryFn: () => fetchDataNode(`countries-list`),
    staleTime: DEFAULT_STALE_TIME,
    cacheTime: DEFAULT_CACHE_TIME,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });
};

export const useCityList = (filters) => {
  return useQuery({
    queryKey: ["cities-list", filters],
    queryFn: () => fetchDataNode(`cities-list`, filters),
    enabled: !!filters?.state_id,
    staleTime: DEFAULT_STALE_TIME,
    cacheTime: DEFAULT_CACHE_TIME,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });
};

export const useCityListWithoutState = (filters) => {
  return useQuery({
    queryKey: ["cities-list", filters],
    queryFn: () => fetchDataNode(`cities-list`, filters),
    staleTime: DEFAULT_STALE_TIME,
    cacheTime: DEFAULT_CACHE_TIME,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });
};

export const useStateList = (filters) => {
  return useQuery({
    queryKey: ["states-list", filters],
    queryFn: () => fetchDataNode(`states-list`, filters),
    enabled: !!filters?.country_id,
    staleTime: DEFAULT_STALE_TIME,
    cacheTime: DEFAULT_CACHE_TIME,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  })
};

export const useVeryApartmentPerson = (filters) => {
  return useQuery({
    queryKey: ["contact-person", filters],
    queryFn: () => fetchDataNode(`contact-person`, filters),
    enabled: !!filters?.mobile_no && !!filters?.apartment_id,
    staleTime: DEFAULT_STALE_TIME,
    cacheTime: DEFAULT_CACHE_TIME,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  })
};

export const useSendOtp = (filters) => {
  return useQuery({
    queryKey: ["send-otp", filters],
    queryFn: () => fetchData(`send-otp`, filters),
    enabled: !!filters?.mobile_no,
    staleTime: DEFAULT_STALE_TIME,
    cacheTime: DEFAULT_CACHE_TIME,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  })
};

export const useZoneList = (filters) => {
  return useQuery({
    queryKey: ["collab-zones", filters],
    queryFn: () => fetchDataNode(`collab-zones`, filters),
    enabled: !!filters?.city_id,
    staleTime: DEFAULT_STALE_TIME,
    cacheTime: DEFAULT_CACHE_TIME,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });
};

export const useGetAllUserDetailList = (options = {}) => {
  return useQuery({
    queryKey: ["user-info"],
    queryFn: async () => {
      const data = await fetchDataNode("details");
      return data;
    },
    ...options,
  });
};

export const useGetOnlineCourseDetail = (filters) => {
  return useQuery({
    queryKey: ["new-course-online", filters],
    queryFn: () => fetchDataNode(`new-course-online`, filters),
    enabled: !!filters?.slug,
    staleTime: DEFAULT_STALE_TIME,
    cacheTime: DEFAULT_CACHE_TIME,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });
};

export const useGetDiscountCouponCode = (filter) => {
  return useQuery({
    queryKey: ["discounts/get-all-discounts", filter],
    queryFn: () => fetchDataNode(`discounts/get-all-discounts`, filter),
    staleTime: DEFAULT_STALE_TIME,
    cacheTime: DEFAULT_CACHE_TIME,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });
};


export const useGetLeaderBoardWinners = (filter) => {
  return useQuery({
    queryKey: ["winners", filter],
    queryFn: () => fetchData(`winners`, filter),
    staleTime: DEFAULT_STALE_TIME,
    cacheTime: DEFAULT_CACHE_TIME,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });
};

export const useGetcandidateVote = (filter) => {
  return useQuery({
    queryKey: ["student-poling-images", filter],
    queryFn: () => fetchDataNode(`student-poling-images`, filter),
    staleTime: DEFAULT_STALE_TIME,
    cacheTime: DEFAULT_CACHE_TIME,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });
};

export const useSchoolList = () => {
  return useQuery({
    queryKey: ["get-allschools-list"],
    queryFn: () => fetchDataNode(`get-allschools-list`),
    staleTime: DEFAULT_STALE_TIME,
    cacheTime: DEFAULT_CACHE_TIME,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });
};

export const useProductList = (filters = {}) => {
  return useInfiniteQuery({
    queryKey: ["allproducts", filters],
    queryFn: async ({ pageParam = 1 }) => {
      return await fetchData("allproducts", { ...filters, page: pageParam }, apiUrlShop);
    },
    getNextPageParam: (lastPage) => {
      const currentPage = lastPage?.pagination?.currentPage || 1;
      const totalPages = lastPage?.pagination?.totalPages || 1;
      return currentPage < totalPages ? currentPage + 1 : undefined;
    },
    staleTime: DEFAULT_STALE_TIME,
    cacheTime: DEFAULT_CACHE_TIME,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });
};



export const useAllAdminCity = (filters) => {
  return useQuery({
    queryKey: ["all-cities-list", filters],
    queryFn: () => fetchDataNode(`all-cities-list`, filters),
    staleTime: DEFAULT_STALE_TIME,
    cacheTime: DEFAULT_CACHE_TIME,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });
};



export const useGetAllSubjectList = (filter) => {
  return useInfiniteQuery({
    queryKey: ["subject/list", filter],
    queryFn: ({ pageParam = 1 }) =>
      fetchDataNode(`subject?page=${pageParam}&limit=10`, filter),
    getNextPageParam: (lastPage) => {
      return lastPage.page < lastPage.totalPages
        ? lastPage.page + 1
        : undefined;
    },
    staleTime: DEFAULT_STALE_TIME,
    cacheTime: DEFAULT_CACHE_TIME,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });
};


export const useReviews = (id, filters = { page: 1, limit: 3 }) => {
  return useQuery({
    queryKey: ["reviews", id, filters],
    queryFn: () => fetchDataNode(`reviews/${id}`, filters),
    enabled: !!id,
    staleTime: DEFAULT_STALE_TIME,
    cacheTime: DEFAULT_CACHE_TIME,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });
};


export const useSkillBoxProducts = (id, page = 1) => {
  return useQuery({
    queryKey: ["skillsbox-products", id, page],
    queryFn: () => fetchData(`categories/${id}/products`, { page, per_page: 20 }, window._env_?.SKILLBOX_PRODUCT),
    enabled: !!id,
    staleTime: DEFAULT_STALE_TIME,
    cacheTime: DEFAULT_CACHE_TIME,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });
};

export const useSkillPreskoolProducts = () => {
  return useQuery({
    queryKey: ["skill-preskool-products"],
    queryFn: () => fetchData(`categories/8/products`, {}, window._env_?.SKILLBOX_PRODUCT),
    staleTime: DEFAULT_STALE_TIME,
    cacheTime: DEFAULT_CACHE_TIME,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });
};

export const useSkillBoxProductsDetails = (id) => {
  return useQuery({
    queryKey: ["skillsbox-products-details", id],
    queryFn: () => fetchData(`products/${id}`, {}, window._env_?.SKILLBOX_PRODUCT),
    staleTime: DEFAULT_STALE_TIME,
    cacheTime: DEFAULT_CACHE_TIME,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });
};


export const useSkillProductCategory = () => {
  return useQuery({
    queryKey: ["skill-product-category"],
    queryFn: () => fetchData(`categories`, {}, window._env_?.SKILLBOX_PRODUCT),
    staleTime: DEFAULT_STALE_TIME,
    cacheTime: DEFAULT_CACHE_TIME,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });
};

export const useAllSkillBoxProducts = () => {
  return useQuery({
    queryKey: ["all-skillsbox-products"],
    queryFn: async () => {
      let allProducts = [];
      let page = 1;
      let totalPages = 1;
      
      do {
        const response = await fetchData(`products`, { per_page: 100, page }, window._env_?.SKILLBOX_PRODUCT);
        allProducts = [...allProducts, ...(response.data || [])];
        totalPages = response.pagination?.pages || 1;
        page++;
      } while (page <= totalPages);

      return { data: allProducts };
    },
    staleTime: DEFAULT_STALE_TIME,
    cacheTime: DEFAULT_CACHE_TIME,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });
};


export const Activitylms = (page = 1) => {
  return useQuery({
    queryKey: ["activity-lms-course", page],
    queryFn: () =>
      fetchDataNode(`/v1/sv/courses/skill2Preskool?page=${page}&limit=20&status=true`),
    staleTime: DEFAULT_STALE_TIME,
    cacheTime: DEFAULT_CACHE_TIME,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });
};
